import { Plus, Loader2, Check, Sparkles, X } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import {
  getAmenitiesByCategory,
  getDefaultAmenityCategories,
} from '../../../lib/defaults/defaultSportsAndAmenities';

interface CompanyAmenity {
  id: string;
  amenity_name: string;
  category_id: string | null;
  company_amenity_categories?: {
    name: string;
  } | null;
}

interface AmenityCategory {
  id: string;
  name: string;
  company_id: string | null;
}

interface AmenitiesStepProps {
  companyId: string;
  locationId: string;
  selectedAmenityIds: string[];
  onSelectionChange: (amenityIds: string[]) => void;
}

export default function AmenitiesStep({
  companyId,
  locationId,
  selectedAmenityIds,
  onSelectionChange,
}: AmenitiesStepProps) {
  const [amenities, setAmenities] = useState<CompanyAmenity[]>([]);
  const [categories, setCategories] = useState<AmenityCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add amenity modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState<'create' | 'template' | null>(null); // 'create' or 'template'
  const [newAmenityName, setNewAmenityName] = useState('');
  const [newAmenityCategory, setNewAmenityCategory] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingItem, setAddingItem] = useState(false);

  // Template selection state
  const [selectedTemplateAmenities, setSelectedTemplateAmenities] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch company amenities with categories
      const { data: amenitiesData, error: amenitiesError } = await supabase
        .from('company_amenities')
        .select('*, company_amenity_categories(name)')
        .eq('company_id', companyId);

      if (amenitiesError) throw amenitiesError;
      setAmenities(amenitiesData || []);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('company_amenity_categories')
        .select('*')
        .eq('company_id', companyId);

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      // If location exists, fetch existing selections
      if (locationId) {
        const { data: existingAmenities, error: existingError } = await supabase
          .rpc('get_location_amenity_ids', { location_id_input: locationId });

        if (!existingError && existingAmenities) {
          const amenityIds = existingAmenities.map((a: { amenity_id: string }) => a.amenity_id);
          onSelectionChange(amenityIds);
        }
      }
    } catch (err: unknown) {
      // Error fetching amenities
      setError(err instanceof Error ? err.message : 'Failed to load amenities');
    } finally {
      setLoading(false);
    }
  }, [companyId, locationId, onSelectionChange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleAmenity = (amenityId: string) => {
    if (selectedAmenityIds.includes(amenityId)) {
      onSelectionChange(selectedAmenityIds.filter(id => id !== amenityId));
    } else {
      onSelectionChange([...selectedAmenityIds, amenityId]);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    setAddingItem(true);

    try {
      const { data, error } = await supabase
        .from('company_amenity_categories')
        .insert({ name: newCategoryName.trim(), company_id: companyId })
        .select()
        .single();

      if (error) throw error;

      setCategories([...categories, data]);
      setNewAmenityCategory(data.id);
      setNewCategoryName('');
      setShowNewCategory(false);
    } catch (err: unknown) {
      // Error adding category
      setError(err instanceof Error ? err.message : 'Failed to add category');
    } finally {
      setAddingItem(false);
    }
  };

  const handleAddAmenity = async () => {
    if (!newAmenityName.trim() || !newAmenityCategory) return;
    setAddingItem(true);

    try {
      const { data, error } = await supabase
        .from('company_amenities')
        .insert({
          amenity_name: newAmenityName.trim(),
          category_id: newAmenityCategory,
          company_id: companyId,
        })
        .select('*, company_amenity_categories(name)')
        .single();

      if (error) throw error;

      setAmenities([...amenities, data]);
      // Auto-select the new amenity
      onSelectionChange([...selectedAmenityIds, data.id]);

      // Reset form
      setNewAmenityName('');
      setNewAmenityCategory('');
      setShowAddModal(false);
    } catch (err: unknown) {
      // Error adding amenity
      setError(err instanceof Error ? err.message : 'Failed to add amenity');
    } finally {
      setAddingItem(false);
    }
  };

  // Group amenities by category
  const groupedAmenities = amenities.reduce<Record<string, CompanyAmenity[]>>((acc, amenity) => {
    const categoryName = amenity.company_amenity_categories?.name || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(amenity);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-rollops-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-3">
          <Sparkles size={24} className="text-rollops-accent" />
        </div>
        <h3 className="text-lg font-semibold text-[#EBFFEE]">Amenities</h3>
        <p className="text-sm text-[#A9A3A3] mt-1">
          Select the amenities available at this location
        </p>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}


      {/* Amenities Selection */}
      {amenities.length === 0 ? (
        <div className="text-center py-8 bg-[#221F1F] rounded-xl border border-[#504A4A]">
          <p className="text-[#A9A3A3] mb-4">No amenities have been added yet.</p>
          <button
            onClick={() => {
              setAddMode(null);
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
          >
            <Plus size={18} />
            Add First Amenity
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedAmenities).map(([categoryName, categoryAmenities]) => (
            <div key={categoryName} className="bg-[#221F1F] rounded-xl p-4 border border-[#504A4A]">
              <h4 className="text-sm font-medium text-[#A9A3A3] mb-3 uppercase tracking-wider">
                {categoryName}
              </h4>
              <div className="flex flex-wrap gap-2">
                {categoryAmenities.map(amenity => {
                  const isSelected = selectedAmenityIds.includes(amenity.id);
                  return (
                    <button
                      key={amenity.id}
                      onClick={() => toggleAmenity(amenity.id)}
                      className={`
                        px-4 py-2 rounded-full text-sm font-medium transition-all
                        ${isSelected
                          ? 'bg-[#9B1421] text-white'
                          : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454] hover:text-[#EBFFEE]'
                        }
                      `}
                    >
                      {isSelected && <Check size={14} className="inline mr-1" />}
                      {amenity.amenity_name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Add Amenity Button */}
          <button
            onClick={() => {
              setAddMode(null);
              setShowAddModal(true);
            }}
            className="w-full py-3 border-2 border-dashed border-[#504A4A] rounded-xl text-[#A9A3A3] hover:border-[#9B1421] hover:text-rollops-accent transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            Add Amenity
          </button>
        </div>
      )}

      {/* Selected Count */}
      <div className="text-center text-sm text-[#A9A3A3]">
        {selectedAmenityIds.length} amenity{selectedAmenityIds.length !== 1 ? 'ies' : ''} selected
      </div>

      {/* Add Amenity Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-[#221F1F] rounded-xl p-6 border border-[#504A4A] ${
            addMode === 'template' ? 'w-full max-w-2xl max-h-[90vh] overflow-y-auto' : 'w-full max-w-md'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#EBFFEE]">
                {addMode === null ? 'Add Amenity' : addMode === 'create' ? 'Create New Amenity' : 'Pick from Template'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddMode(null);
                  setNewAmenityName('');
                  setNewAmenityCategory('');
                  setShowNewCategory(false);
                  setNewCategoryName('');
                  setSelectedTemplateAmenities(new Set());
                }}
                className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Mode Selection - Show when no mode selected */}
            {addMode === null && (
              <div className="space-y-4">
                <p className="text-sm text-[#A9A3A3] mb-4">
                  Choose how you want to add an amenity:
                </p>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => setAddMode('create')}
                    className="p-4 bg-[#504A4A] hover:bg-[#5A5454] rounded-lg text-left transition-colors border border-transparent hover:border-[#9B1421]/50"
                  >
                    <div className="font-semibold text-[#EBFFEE] mb-1">Create New</div>
                    <div className="text-sm text-[#A9A3A3]">Create a custom amenity with your own name and category</div>
                  </button>
                  <button
                    onClick={() => setAddMode('template')}
                    className="p-4 bg-[#504A4A] hover:bg-[#5A5454] rounded-lg text-left transition-colors border border-transparent hover:border-[#9B1421]/50"
                  >
                    <div className="font-semibold text-[#EBFFEE] mb-1">Pick from Template</div>
                    <div className="text-sm text-[#A9A3A3]">Select from predefined amenities. You can pick individual items - selecting one doesn&apos;t add all.</div>
                  </button>
                </div>
              </div>
            )}

            {/* Create New Mode */}
            {addMode === 'create' && (
              <div className="space-y-4">
                {/* Amenity Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#EBFFEE]">Amenity Name</label>
                  <input
                    type="text"
                    value={newAmenityName}
                    onChange={(e) => setNewAmenityName(e.target.value)}
                    placeholder="e.g., Sauna, Cold Plunge"
                    className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                  />
                </div>

                {/* Category Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#EBFFEE]">Category</label>
                  {!showNewCategory ? (
                    <div className="space-y-2">
                      <select
                        value={newAmenityCategory}
                        onChange={(e) => {
                          if (e.target.value === 'new') {
                            setShowNewCategory(true);
                          } else {
                            setNewAmenityCategory(e.target.value);
                          }
                        }}
                        className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                        <option value="new">+ Create New Category</option>
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="New category name"
                        className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowNewCategory(false);
                            setNewCategoryName('');
                          }}
                          className="flex-1 py-2 bg-[#504A4A] hover:bg-[#5A5454] text-white rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddCategory}
                          disabled={!newCategoryName.trim() || addingItem}
                          className="flex-1 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] text-white rounded-lg"
                        >
                          {addingItem ? 'Creating...' : 'Create Category'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Template Mode */}
            {addMode === 'template' && (
              <div className="space-y-4">
                <p className="text-sm text-[#A9A3A3] mb-4">
                  Select amenities from the template. You can pick individual items - selecting one doesn&apos;t add all.
                </p>
                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                  {getDefaultAmenityCategories().map(category => {
                    const categoryAmenities = getAmenitiesByCategory(category);
                    const existingAmenityNames = new Set(amenities.map(a => a.amenity_name.toLowerCase()));
                    
                    return (
                      <div key={category} className="bg-[#363232] rounded-lg p-4 border border-[#504A4A]">
                        <h4 className="text-sm font-semibold text-[#EBFFEE] mb-3 uppercase tracking-wider">
                          {category}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {categoryAmenities.map(amenity => {
                            const isSelected = selectedTemplateAmenities.has(`${category}::${amenity.name}`);
                            const alreadyExists = existingAmenityNames.has(amenity.name.toLowerCase());
                            const key = `${category}::${amenity.name}`;
                            
                            return (
                              <button
                                key={key}
                                onClick={() => {
                                  if (alreadyExists) return;
                                  const newSet = new Set(selectedTemplateAmenities);
                                  if (isSelected) {
                                    newSet.delete(key);
                                  } else {
                                    newSet.add(key);
                                  }
                                  setSelectedTemplateAmenities(newSet);
                                }}
                                disabled={alreadyExists}
                                className={`
                                  px-3 py-1.5 text-sm rounded-full transition-all flex items-center gap-1
                                  ${alreadyExists
                                    ? 'bg-[#504A4A]/50 text-[#A9A3A3]/50 cursor-not-allowed'
                                    : isSelected
                                      ? 'bg-[#9B1421] text-white'
                                      : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454] hover:text-[#EBFFEE]'
                                  }
                                `}
                                title={alreadyExists ? 'This amenity already exists' : ''}
                              >
                                {isSelected && <Check size={14} />}
                                {amenity.name}
                                {alreadyExists && <span className="text-xs ml-1">(exists)</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedTemplateAmenities.size > 0 && (
                  <div className="bg-[#9B1421]/20 border border-[#9B1421]/50 rounded-lg p-3">
                    <p className="text-sm text-[#EBFFEE]">
                      {selectedTemplateAmenities.size} amenit{selectedTemplateAmenities.size !== 1 ? 'ies' : 'y'} selected
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex gap-3 mt-6">
              {addMode !== null && (
                <button
                  onClick={() => {
                    setAddMode(null);
                    setNewAmenityName('');
                    setNewAmenityCategory('');
                    setShowNewCategory(false);
                    setNewCategoryName('');
                    setSelectedTemplateAmenities(new Set());
                  }}
                  className="px-4 py-2 bg-[#504A4A] hover:bg-[#5A5454] text-white rounded-lg transition-colors"
                >
                  Back
                </button>
              )}
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddMode(null);
                  setNewAmenityName('');
                  setNewAmenityCategory('');
                  setShowNewCategory(false);
                  setNewCategoryName('');
                  setSelectedTemplateAmenities(new Set());
                }}
                className={`${addMode === null ? 'flex-1' : ''} py-2 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white rounded-lg`}
              >
                Cancel
              </button>
              {addMode === 'create' && (
                <button
                  onClick={handleAddAmenity}
                  disabled={!newAmenityName.trim() || !newAmenityCategory || addingItem}
                  className="flex-1 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] text-white rounded-lg"
                >
                  {addingItem ? 'Adding...' : 'Add Amenity'}
                </button>
              )}
              {addMode === 'template' && (
                <button
                  onClick={async () => {
                    if (selectedTemplateAmenities.size === 0) return;
                    
                    setAddingItem(true);
                    try {
                      // Get or create categories first
                      const categoryMap = new Map<string, string>();
                      
                      for (const key of selectedTemplateAmenities) {
                        const [category] = key.split('::');
                        if (!categoryMap.has(category)) {
                          // Check if category exists
                          let categoryId = categories.find(c => c.name === category)?.id;
                          
                          if (!categoryId) {
                            // Create category
                            const { data: newCat, error: catError } = await supabase
                              .from('company_amenity_categories')
                              .insert({ name: category, company_id: companyId })
                              .select()
                              .single();
                            
                            if (catError) throw catError;
                            categoryId = newCat.id;
                            setCategories(prev => [...prev, newCat]);
                          }
                          
                          categoryMap.set(category, categoryId);
                        }
                      }
                      
                      // Add selected amenities
                      const amenitiesToAdd = Array.from(selectedTemplateAmenities).map(key => {
                        const [category, name] = key.split('::');
                        const categoryId = categoryMap.get(category);
                        if (!categoryId) {
                          throw new Error(`Category not found: ${category}`);
                        }
                        return {
                          amenity_name: name,
                          category_id: categoryId,
                          company_id: companyId,
                        };
                      });
                      
                      const { data: newAmenities, error: amenitiesError } = await supabase
                        .from('company_amenities')
                        .insert(amenitiesToAdd)
                        .select('*, company_amenity_categories(name)');
                      
                      if (amenitiesError) throw amenitiesError;
                      
                      setAmenities(prev => [...prev, ...newAmenities]);
                      
                      // Auto-select the newly added amenities for the current location
                      const newAmenityIds = newAmenities.map(amenity => amenity.id);
                      const updatedSelections = [...selectedAmenityIds, ...newAmenityIds];
                      onSelectionChange(updatedSelections);
                      
                      setShowAddModal(false);
                      setAddMode(null);
                      setSelectedTemplateAmenities(new Set());
                    } catch (err: unknown) {
                      // Error adding template amenities
                      setError(err instanceof Error ? err.message : 'Failed to add amenities');
                    } finally {
                      setAddingItem(false);
                    }
                  }}
                  disabled={selectedTemplateAmenities.size === 0 || addingItem}
                  className="flex-1 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white rounded-lg"
                >
                  {addingItem ? 'Adding...' : `Add ${selectedTemplateAmenities.size} Selected`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

